/**
 * Technology Tag Modal Component
 *
 * Modal for adding tags to technologies with optimistic updates and
 * duplicate prevention.
 *
 * Features:
 * - Optimistic updates for instant UI feedback
 * - Duplicate tag prevention (case-insensitive)
 * - Granular error reporting per technology
 * - Automatic cache invalidation and refresh
 *
 * Improvements aligned with vulnerability/asset implementations:
 * - Uses optimistic updates for better UX
 * - Prevents duplicate tags
 * - Better error handling with detailed feedback
 */

import { toast } from 'sonner';

import { useAxios } from '@/hooks/useAxios';
import { queryClient } from '@/queryclient';
import { AddTag } from '@/sections/AddTag';
import { useMutation } from '@/utils/api';
import {
  tagAddedError,
  tagAddedSuccess,
  tagValidationError,
} from '@/utils/tagToastMessages';
import { validateTagInput } from '@/utils/tagValidation';

interface Props {
  open: boolean;
  onClose: () => void;
  technologyKeys: string[];
  currentTags?: string[]; // Deprecated: No longer used, always fetches from backend
  onSuccess?: () => void;
}

interface TagOperationResult {
  succeeded: number;
  failed: number;
  duplicates: number;
  results: Array<{ key: string; skipped?: boolean; reason?: string }>;
}

/**
 * Modal component for adding tags to technologies
 *
 * Uses shared AddTag component for consistent UX across the platform.
 * Handles both single technology (from drawer) and bulk operations (from table).
 *
 * Note: Always fetches current tags from backend to prevent data loss.
 * The currentTags prop is deprecated and no longer used.
 *
 * Example usage:
 * ```tsx
 * <TechnologyTag
 *   open={modalState.tagModal}
 *   onClose={modalHandlers.closeTagModal}
 *   technologyKeys={selectedKeys}
 *   onSuccess={clearSelection}
 * />
 * ```
 *
 * @param props - Component props
 * @returns Modal component
 */
export const TechnologyTag = ({
  open,
  onClose,
  technologyKeys,
  currentTags: _currentTags = [], // Deprecated: No longer used, always fetches from backend
  onSuccess,
}: Props) => {
  const axios = useAxios();

  const { mutate: updateTechnologies, status: updatingTechnologies } =
    useMutation<TagOperationResult, Error, string>({
      defaultErrorMessage: 'Failed to add tag',
      mutationFn: async (newTag: string) => {
        // Fetch current state for all technologies
        const promises = technologyKeys.map(async techKey => {
          try {
            // ALWAYS fetch current technology to get both tags AND status
            // Backend requires status to be included when updating tags
            const currentTech = await axios.get(`/my`, {
              params: {
                label: 'technology',
                key: techKey,
                exact: true,
              },
            });

            const tech = currentTech.data.technologies?.[0];
            if (!tech) {
              throw new Error(`Technology not found: ${techKey}`);
            }

            const existingTags = tech.tags || [];
            const currentStatus = tech.status || 'A';

            // Check for duplicate (case-insensitive)
            const isDuplicate = existingTags.some(
              (tag: string) => tag.toLowerCase() === newTag.toLowerCase()
            );

            if (isDuplicate) {
              return {
                key: techKey,
                skipped: true,
                reason: 'Tag already exists',
              };
            }

            const payload = {
              key: techKey,
              status: currentStatus, // Include current status (required by backend)
              tags: [...existingTags, newTag],
            };

            await axios.put('/technology', payload);

            return {
              key: techKey,
              skipped: false,
            };
          } catch (error) {
            console.error(`[TechnologyTagModal] Failed for ${techKey}:`, error);
            throw error;
          }
        });

        const results = await Promise.allSettled(promises);

        // Process results
        const succeeded = results.filter(
          r => r.status === 'fulfilled' && !r.value.skipped
        ).length;

        const duplicates = results.filter(
          r => r.status === 'fulfilled' && r.value.skipped
        ).length;

        const failed = results.filter(r => r.status === 'rejected').length;

        // Log failures for debugging
        if (failed > 0) {
          const failedResults = results
            .filter((r): r is PromiseRejectedResult => r.status === 'rejected')
            .map((r, i) => ({
              key: technologyKeys[i],
              error: r.reason?.message || 'Unknown error',
            }));
          console.error(
            '[TechnologyTagModal] Failed operations:',
            failedResults
          );
        }

        return {
          succeeded,
          duplicates,
          failed,
          results: results.map((r, i) => {
            if (r.status === 'fulfilled') {
              return r.value;
            }
            return { key: technologyKeys[i], skipped: false };
          }),
        };
      },
      onMutate: async newTag => {
        // Cancel outgoing refetches to prevent overwriting optimistic update
        await queryClient.cancelQueries({
          predicate: query => {
            const queryKey = query.queryKey as string[];
            return queryKey[0] === 'MY' && queryKey[1] === 'technology';
          },
        });

        // Snapshot previous value for rollback
        const previousData = queryClient.getQueriesData({
          predicate: query => {
            const queryKey = query.queryKey as string[];
            return queryKey[0] === 'MY' && queryKey[1] === 'technology';
          },
        });

        // Optimistically update all technology queries
        queryClient.setQueriesData(
          {
            predicate: query => {
              const queryKey = query.queryKey as string[];
              return queryKey[0] === 'MY' && queryKey[1] === 'technology';
            },
          },
          (old: any) => {
            if (!old?.technologies) return old;

            return {
              ...old,
              technologies: old.technologies.map((tech: any) => {
                if (technologyKeys.includes(tech.key)) {
                  const existingTags = tech.tags || [];
                  // Only add if not duplicate (case-insensitive)
                  const isDuplicate = existingTags.some(
                    (tag: string) => tag.toLowerCase() === newTag.toLowerCase()
                  );
                  if (isDuplicate) return tech;

                  return {
                    ...tech,
                    tags: [...existingTags, newTag],
                  };
                }
                return tech;
              }),
            };
          }
        );

        return { previousData };
      },
      onError: (_err: Error, _newTag: string, context: unknown) => {
        const contextData = context as
          | { previousData?: Array<[any, any]> }
          | undefined;
        // Rollback on error
        if (contextData?.previousData) {
          contextData.previousData.forEach(([queryKey, data]: [any, any]) => {
            queryClient.setQueryData(queryKey, data);
          });
        }
        toast.error('Failed to add tag. Changes have been reverted.');
      },
      onSettled: async () => {
        // Invalidate queries after mutation completes
        // Use predicate-based invalidation to catch ALL technology queries
        await queryClient.invalidateQueries({
          predicate: query => {
            const queryKey = query.queryKey as string[];
            return queryKey[0] === 'MY' && queryKey[1] === 'technology';
          },
          refetchType: 'all', // Use 'all' to force refetch of drawer and table
        });
      },
    });

  const handleSubmit = async (tag: string) => {
    // Validate input
    const validation = validateTagInput(tag);
    if (!validation.isValid) {
      toast.error(tagValidationError(validation.error!));
      return;
    }

    try {
      const result = await new Promise<TagOperationResult>(
        (resolve, reject) => {
          updateTechnologies(validation.sanitized!, {
            onSuccess: resolve,
            onError: reject,
          });
        }
      );

      // Show appropriate feedback based on results
      if (
        result.succeeded > 0 &&
        result.failed === 0 &&
        result.duplicates === 0
      ) {
        const count = result.succeeded > 1 ? result.succeeded : undefined;
        toast.success(tagAddedSuccess('technology', count));
        onSuccess?.();
        onClose();
      } else if (
        result.succeeded > 0 &&
        (result.failed > 0 || result.duplicates > 0)
      ) {
        let message = `Tag added to ${result.succeeded} ${result.succeeded > 1 ? 'technologies' : 'technology'}`;
        if (result.duplicates > 0) {
          message += `, skipped ${result.duplicates} (already tagged)`;
        }
        if (result.failed > 0) {
          message += `, failed ${result.failed}`;
        }
        toast.warning(message);
        onSuccess?.();
        onClose();
      } else if (result.duplicates === technologyKeys.length) {
        toast.warning('All selected technologies already have this tag');
      } else {
        toast.error(tagAddedError('technology'));
      }
    } catch (error: any) {
      console.error('[TechnologyTagModal] Tag operation failed:', error);
      // Error toast already shown in onError
    }
  };

  return (
    <AddTag
      open={open}
      onClose={onClose}
      disabled={updatingTechnologies === 'pending'}
      onSubmit={handleSubmit}
      description="Tags help organize and classify technologies in your attack surface."
    />
  );
};
